// Network helpers for the Oireachtas event-day tools (Workstream B / B1): draw,
// check-in and the paper PDFs. Thin wrappers over serviceConsumer so the screens
// stay declarative. PDFs are fetched as a blob (the shared GET path doesn't set
// an arraybuffer responseType) with the same auth headers and downloaded.
import axios from "axios";
import { serviceConsumer } from "./ServiceConsumer";

const base = () => `${process.env.REACT_APP_BASE_URL}/oireachtas`;

export const getDrawDashboard = (eventId) =>
  serviceConsumer("GET", `${base()}/${eventId}/draws`);

export const getDraw = (eventId, compId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/draw`);

export const generateDraw = (eventId, compId, seed, resetDownstream = false) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/draw`, {
    seed,
    ...(resetDownstream ? { resetDownstream: true } : {}),
  });

export const lockDraw = (eventId, compId) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/draw/lock`, {});

// One-and-done bulk flow (locked 2026-07-04): draw every comp from one master
// witness phrase (blank = server-generated), lock every draft, and fetch all
// current draws hydrated for the Excel export.
export const generateAllDraws = (eventId, seed) =>
  serviceConsumer("POST", `${base()}/${eventId}/draws/generate-all`, { seed });

export const lockAllDraws = (eventId) =>
  serviceConsumer("POST", `${base()}/${eventId}/draws/lock-all`, {});

export const getDrawsExport = (eventId) =>
  serviceConsumer("GET", `${base()}/${eventId}/draws/export`);

export const getCheckIn = (eventId, compId, round) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/checkin`, { round });

export const setCheckIn = (eventId, compId, round, marks) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/checkin`, { round, marks });

// Recall-round set change at the side stage: body { round, ref, danceChoice,
// speed }. Checking the dancer in is what confirms the set.
export const setCheckInSet = (eventId, compId, body) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/checkin/set`, body);

// ---- B2: tabulation (panels, score entry, mishaps, sign-off, preview) ------

export const getPanels = (eventId, compId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/panels`);

// Single-slot upsert — the panel builds up as sheets are entered.
export const setPanelSlot = (eventId, compId, round, slot, person_id) =>
  serviceConsumer("PUT", `${base()}/${eventId}/competitions/${compId}/panels/slot`, {
    round,
    slot,
    person_id,
  });

export const setPanel = (eventId, compId, round, slots) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/panels`, { round, slots });

export const getScoreGrid = (eventId, compId, round) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/scores`, { round });

// person_id: the judge this sheet belongs to — the server refuses the write
// if the seat has been reassigned since (attribution guard). correctPublished:
// the admin confirmed this changes a published result (else the server
// answers 409 code PUBLISHED_CORRECTION).
// expectSheet: the judge's sheet as it was when typing started
// (sheetFingerprint); the save is refused (SHEET_CHANGED) if someone else
// saved it meanwhile. With no scores, the call only checks.
export const enterScores = (eventId, compId, round, judgeSlot, scores, person_id, correctPublished = false, expectSheet) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/scores`, {
    round,
    judgeSlot,
    scores,
    ...(person_id ? { person_id } : {}),
    ...(correctPublished ? { correctPublished: true } : {}),
    ...(typeof expectSheet === "string" ? { expectSheet } : {}),
  });

// A saved sheet under the wrong judge moves to the right one.
export const moveSheet = (eventId, compId, round, slot, person_id) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/panels/move`, { round, slot, person_id });

// The server's error code (e.g. PUBLISHED_CORRECTION) from a serviceConsumer error.
export const errorCode = (e) => e?.error?.response?.data?.code || "";

export const setMishap = (eventId, compId, body) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/mishaps`, body);

export const recordSignOff = (eventId, compId, round, judgeSlot) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/signoff`, {
    round,
    judgeSlot,
  });

export const getTabulation = (eventId, compId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/tabulation`);

// ---- B3: results (recalls, placements, qualification, publish) -------------

export const getTabulationStatus = (eventId) =>
  serviceConsumer("GET", `${base()}/${eventId}/tabulation-status`);

// Wipe one judge column for one round (blocked server-side while published).
export const clearScoreColumn = (eventId, compId, round, judgeSlot) =>
  serviceConsumer(
    "DELETE",
    `${base()}/${eventId}/competitions/${compId}/scores?round=${round}&judgeSlot=${judgeSlot}`
  );

export const getResults = (eventId, compId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/results`);

// The full results table as the public results page shows it (View Scores'
// Results tab): the live result once every sheet is signed, the published
// snapshot once published.
export const getResultsPreview = (eventId, compId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitions/${compId}/results/preview`);

// A tabulator signs off the overall result (stage "recall" | "results"):
// required before the announcer print and publishing. version = the
// getResults `versions[stage]` of the result they were shown; the server
// refuses the check if anything changed since.
export const checkResult = (eventId, compId, stage, version) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/result-check`, { stage, version });

export const publishRecall = (eventId, compId, unpublish = false) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/recall/publish`, {
    unpublish,
  });

export const publishResults = (eventId, compId, unpublish = false) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/results/publish`, {
    unpublish,
  });

// A one-round competition's places list (Devin 2026-11-04): the placed
// numbers in numerical order, published before the results.
export const publishPlaces = (eventId, compId, unpublish = false) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/places/publish`, {
    unpublish,
  });

export const drawRoundAward = (eventId, compId, round, seed) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/round-award`, {
    round,
    seed,
  });

// The regional director's extra NAIDC qualifiers (0-2) past the Rule 12 count.
export const setNaidcExtra = (eventId, compId, extra) =>
  serviceConsumer("POST", `${base()}/${eventId}/competitions/${compId}/naidc-extra`, { extra });

// B4: event-level master gate — parents see NOTHING on P1 until this flips,
// regardless of per-comp publish states (spec §9).
export const setResultsMasterGate = (eventId, published) =>
  serviceConsumer("POST", `${base()}/${eventId}/results/master-gate`, { published });

// Running orders (names only, before the 7:00 am numbers release) for
// families and teachers: the Draw screen's switch (card f6c2c874).
export const setNamesListsShown = (eventId, shown) =>
  serviceConsumer("POST", `${base()}/${eventId}/names-lists`, { shown });

export const getStatusFlags = (eventId) =>
  serviceConsumer("GET", `${base()}/${eventId}/status-flags`);

export const setStatusFlags = (eventId, body) =>
  serviceConsumer("POST", `${base()}/${eventId}/status-flags`, body);

// ---- B5: reporting & reconciliation (spec §10) -----------------------------

export const getReport = (eventId, report, params) =>
  serviceConsumer("GET", `${base()}/${eventId}/reports/${report}`, params);

// Competitors screen: dancer-centric roster (solo ∪ team comps + flags).
export const getEventCompetitors = (eventId) =>
  serviceConsumer("GET", `${base()}/${eventId}/competitors`);

// Stripe payout account: list the organizer's connected accounts + assign one
// to the event (server verifies the account belongs to the event's organizer).
export const listConnectAccounts = (organizerId) =>
  serviceConsumer("POST", `${process.env.REACT_APP_BASE_URL}/stripe/list-connect-accounts`, {
    organizer_id: organizerId,
  });

export const setPayoutAccount = (eventId, connectedAccountId) =>
  serviceConsumer("POST", `${base()}/${eventId}/payout-account`, { connectedAccountId });

// Admin entry corrections (pre-draw; server enforces the locked-draw guard).
export const setEntryCompetitions = (eventId, dancerId, competitions) =>
  serviceConsumer("PUT", `${base()}/${eventId}/entries/${dancerId}/competitions`, { competitions });

export const deleteEventEntry = (eventId, dancerId) =>
  serviceConsumer("DELETE", `${base()}/${eventId}/entries/${dancerId}`);

export const downloadCompetitorsCsv = async (eventId) => {
  const res = await axios.get(`${base()}/${eventId}/competitors?format=csv`, {
    responseType: "blob",
    headers: {
      Authorization:
        "Basic " +
        window.btoa(`${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`),
      accesstoken: localStorage.getItem("accessToken"),
    },
  });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = "competitors.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// CSV export — blob download with auth headers (same reason as the PDFs:
// the shared GET path can't do non-JSON responses).
export const downloadReportCsv = async (eventId, report, params, filename) => {
  const qs = new URLSearchParams({ ...(params || {}), format: "csv" }).toString();
  const res = await axios.get(`${base()}/${eventId}/reports/${report}?${qs}`, {
    responseType: "blob",
    headers: {
      Authorization:
        "Basic " +
        window.btoa(`${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`),
      accesstoken: localStorage.getItem("accessToken"),
    },
  });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || `${report}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// A refused PDF request answers with a JSON body, which arrives as a Blob
// here. Rethrow it as { messages } (the serviceConsumer error shape) so the
// screens can show the server's reason rather than a guess.
const pdfError = async (error) => {
  let message = "";
  try {
    const data = error?.response?.data;
    const text = data && typeof data.text === "function" ? await data.text() : "";
    message = (text && JSON.parse(text).message) || "";
  } catch (e) {
    message = "";
  }
  return { messages: message || "The PDF could not be made — check the connection and try again" };
};

// How long an opened PDF's blob stays readable: its tab keeps showing the
// document after this; a reload of that tab, or a Save in a viewer that
// re-reads the file, works within it. (A Save from the tab gets the browser's
// own name for the file: a blob URL carries no file name.)
const PDF_TAB_KEEP_MS = 60 * 60 * 1000;

// Fetch a protected PDF and open it in a new tab (Devin 2026-10-28: prints
// open in another tab, not as a download). The tab is opened before the
// fetch, while the click still counts, so pop-up blockers let it through;
// a browser that blocks it anyway gets the old download (`filename`).
const showPdf = async (url, filename) => {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.document.write('<p style="font-family: sans-serif">Preparing the PDF…</p>');
    tab.document.close(); // Safari keeps loading an open document
  }
  let res;
  try {
    res = await axios.get(url, {
      responseType: "blob",
      headers: {
        Authorization:
          "Basic " +
          window.btoa(`${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD}`),
        accesstoken: localStorage.getItem("accessToken"),
      },
    });
  } catch (error) {
    if (tab) tab.close();
    throw await pdfError(error);
  }
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  // a tab closed while the PDF was being made gets the download instead (the
  // print may already count, e.g. a sign-off sheet now marked printed)
  if (tab && !tab.closed) {
    tab.location.href = blobUrl;
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), PDF_TAB_KEEP_MS);
    return;
  }
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "output.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// A protected competition PDF, opened in a new tab. `path` is the tail after
// the event/competition (e.g. `cards.pdf`, `checkin.pdf?round=3`).
export const downloadEventDayPdf = (eventId, compId, path, filename) =>
  showPdf(`${base()}/${eventId}/competitions/${compId}/${path}`, filename);

// Event-level PDF (e.g. `results-book.pdf`), opened the same way.
export const downloadEventPdf = (eventId, path, filename) => showPdf(`${base()}/${eventId}/${path}`, filename);
